<template>
  <div class="px-8">
    <!-- this list shows when new items are added -->
    <div
      v-if="checklists.length || checklistsToBeSaved.length"
      class="border-none shared-table-container"
    >
      <div class="shared-table-innermost-container" :style="{ maxHeight: '8.5rem' }">
        <table class="shared-table">
          <thead>
            <tr class="bg-transparent border-none shared-table-headings">
              <th
                v-for="(header, index) in $options.tableHeaders"
                :key="`heading-${index}`"
                class="shared-table-headings-heading"
              >
                <div class="flex items-center justify-center">
                  {{ header }}
                </div>
              </th>
              <th class="shared-table-headings-heading"></th>
            </tr>
          </thead>
          <tbody>
            <!-- checklist existing in database -->
            <tr
              v-for="(r, index) in checklists"
              :key="`checklist-${index}`"
              class="shared-table-cells"
            >
              <td class="shared-table-cells-cell">
                <div class="w-40 truncate">{{ r.order }}</div>
              </td>
              <td class="shared-table-cells-cell">
                <span class="inline-block w-16 py-1 text-white bg-green-500 rounded">saved</span>
              </td>
              <td class="shared-table-cells-cell">
                <div class="w-40 truncate">{{ r.text }}</div>
              </td>
              <td class="shared-table-cells-cell">
                <div class="w-40 truncate">{{ r.link_text }}</div>
              </td>
              <td class="shared-table-cells-cell">
                <div class="w-40 truncate">{{ r.link_video }}</div>
              </td>
              <td class="shared-table-cells-cell">
                <div class="w-40 truncate">{{ r.thumbnail_link }}</div>
              </td>
              <td class="shared-table-cells-cell">
                <div class="flex justify-center">
                  <button
                    class="mr-2 shared-table-cells-button round edit"
                    @click="editCheckList('saved', r)"
                  >
                    <VSvgIcon name="pencil" class="h-5" />
                  </button>
                  <button
                    class="shared-table-cells-button round delete"
                    @click="handleDeleteCheckList(r.id)"
                  >
                    <VSvgIcon name="trash" class="h-5" />
                  </button>
                </div>
              </td>
            </tr>
            <!-- checklist yet to be saved in database -->
            <tr
              v-for="(r, index) in checklistsToBeSaved"
              :key="`checklist-to-be-saved-${index}`"
              class="shared-table-cells"
            >
              <td class="shared-table-cells-cell">
                <div class="w-40 truncate">{{ r.order }}</div>
              </td>
              <td class="shared-table-cells-cell">
                <span class="inline-block w-16 py-1 text-white rounded bg-ricoma-gray-400">
                  draft
                </span>
              </td>
              <td class="shared-table-cells-cell">{{ r.text }}</td>
              <td class="shared-table-cells-cell">
                {{ r.link_text }}
              </td>
              <td class="shared-table-cells-cell">
                <div class="w-40 truncate">{{ r.link_video }}</div>
              </td>
              <td class="shared-table-cells-cell">
                <div class="flex justify-center">
                  <button
                    class="mr-2 shared-table-cells-button round edit"
                    @click="editCheckList('draft', r)"
                  >
                    <VSvgIcon name="pencil" class="h-5" />
                  </button>
                  <button
                    class="shared-table-cells-button round delete"
                    @click="handleRemoveCheckList(r.text)"
                  >
                    <VSvgIcon name="trash" class="h-5" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <form
      @submit.prevent=""
      :class="[isEditable && 'p-4 mb-4 border-2 border-ricoma-orange-300 rounded shadow-lg']"
    >
      <div class="flex">
        <div class="w-6/12 pr-4">
          <VFormInput
            placeholder="Order"
            label="Order*"
            :onlyDigits="true"
            :error="errors.order"
            :reset="resetField"
            :initialValue="checklistEditSaved.order || checklistEditDraft.order"
            @update="value => handleSetField('order', value)"
          />
        </div>
        <div class="w-6/12 pr-4">
          <VFormInput
            placeholder="Enter Text"
            label="Text*"
            :error="errors.text"
            :reset="resetField"
            :initialValue="checklistEditSaved.text || checklistEditDraft.text"
            @update="value => handleSetField('text', value)"
          />
        </div>
        <div class="w-6/12 pr-4">
          <VFormInput
            placeholder="Enter Text Link"
            label="Link Text"
            :error="errors.link_text"
            :reset="resetField"
            :initialValue="checklistEditSaved.link_text || checklistEditDraft.link_text"
            @update="value => handleSetField('link_text', value)"
          />
        </div>
      </div>
      <div class="flex">
        <div class="w-6/12 pr-4">
          <VFormInput
            placeholder="Enter Link Video"
            label="Link Video"
            :error="errors.link_video"
            :reset="resetField"
            :initialValue="checklistEditSaved.link_video || checklistEditDraft.link_video"
            @update="value => handleSetField('link_video', value)"
          />
        </div>
        <div class="w-6/12 pr-4">
          <VFormInput
            placeholder="Enter Thumbnail Link"
            label="Thumbnail Link"
            :error="errors.thumbnail_link"
            :reset="resetField"
            :initialValue="checklistEditSaved.thumbnail_link || checklistEditDraft.thumbnail_link"
            @update="value => handleSetField('thumbnail_link', value)"
          />
        </div>
      </div>
      <div>
        <button
          v-if="!isEditable"
          type="submit"
          class="flex items-center justify-center w-full p-4 text-sm font-semibold text-white uppercase bg-gray-900 rounded"
          @click="handleAddCheckList"
        >
          <VSvgIcon name="plus" class="mr-2" /> Add checklist
        </button>
        <button
          v-else
          type="submit"
          class="flex items-center justify-center w-full p-4 text-sm font-semibold text-white uppercase bg-gray-900 rounded"
          @click="handleEditCheckList"
        >
          <VSvgIcon name="plus" class="mr-2" /> Edit checklist
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import VFormInput from '@/components/base/VFormInput';
import VSvgIcon from '@/components/base/VSvgIcon';

import { formMixin } from '@/mixins';

export default {
  name: 'PreTrainingFormCheckLists',
  components: { VFormInput, VSvgIcon },
  props: {
    language: {
      type: Object,
      required: false,
      default: () => ({ code: 'en' }),
    },
  },
  mixins: [
    formMixin({ order: 0, text: '', link_text: '', link_video: '', thumbnail_link: '' }, [
      'link_text',
      'link_video',
      'thumbnail_link',
    ]),
  ],
  data() {
    return {
      resetField: false,
      checklists: [],
      checklistsToBeSaved: [],
      checklistEditSaved: {},
      checklistEditDraft: {},
    };
  },
  watch: {
    checklistsToBeSaved(checklists) {
      this.$emit('update', checklists);
    },
    async language() {
      this.checklists = await this.fetchChecklists(
        this.$http,
        this.$route.params.id,
        this.language.code,
      );
    },
  },
  computed: {
    editableDraft() {
      return !!Object.keys(this.checklistEditDraft).length;
    },
    editableSaved() {
      return !!Object.keys(this.checklistEditSaved).length;
    },
    isEditable() {
      return this.editableDraft || this.editableSaved;
    },
  },
  methods: {
    editCheckList(type, checklist) {
      type === 'draft'
        ? ((this.checklistEditDraft = checklist), (this.checklistEditSaved = {}))
        : ((this.checklistEditSaved = checklist), (this.checklistEditDraft = {}));
    },
    filterCheckLists(propName, propValue) {
      return data => data[propName] !== propValue;
    },
    async handleAddCheckList() {
      const submitted = await this.handleSubmit(
        ({ order, text, link_text, link_video, thumbnail_link }) => {
          const { code: lang_code } = this.language;
          this.checklistsToBeSaved = this.checklistsToBeSaved.concat({
            order,
            text,
            link_text,
            link_video,
            thumbnail_link,
            lang_code,
          });
          this.resetField = true;
        },
      )();

      if (submitted) this.resetField = false;
    },
    async handleEditCheckList() {
      const submitted = await this.handleSubmit(
        async ({ order, text, link_text, link_video, thumbnail_link }) => {
          if (this.editableDraft) {
            this.checklistsToBeSaved = this.checklistsToBeSaved.map(checklist =>
              checklist.text === this.checklistEditDraft.text
                ? { ...checklist, order, text, link_text, link_video, thumbnail_link }
                : checklist,
            );
          } else {
            const formData = new FormData();
            // append each value to form data
            Object.entries(this.checklistEditSaved).forEach(([key, value]) => {
              if (value === null) {
                value = '';
                formData.append(key, value);
              }
              formData.append(key, value);
            });
            // overide following fields with latest chages
            if (order) formData.set('order', order);
            if (text) formData.set('text', text);
            if (link_text) formData.set('link_text', link_text);
            if (link_video) formData.set('link_video', link_video);
            if (thumbnail_link) formData.set('thumbnail_link', thumbnail_link);

            // append lang_code
            formData.append('lang_code', this.language.code);

            const videoId = this.$route.params.id;
            const res = await this.$http.post(
              `/admin/pre-training-videos/${videoId}/checklists/${this.checklistEditSaved.id}`,
              formData,
            );
            const checklist = res.data.data;

            // update checklist array with new changes
            this.checklists = this.checklists.map(r => (r.id === checklist.id ? checklist : r));
          }

          // reset fields
          this.resetField = true;
          this.checklistEditDraft = {};
          this.checklistEditSaved = {};
        },
      )();

      if (submitted) this.resetField = false;
    },
    handleRemoveCheckList(text) {
      this.checklistsToBeSaved = this.checklistsToBeSaved.filter(
        this.filterCheckLists('text', text),
      );
    },
    async handleDeleteCheckList(id) {
      const videoId = this.$route.params.id;
      await this.$http.delete(`/admin/pre-training-videos/${videoId}/checklists/${id}`);
      this.checklists = this.checklists.filter(this.filterCheckLists('id', id));
    },
    async fetchChecklists(http, videoId, lang_code) {
      const { data } = await this.$http.get(`/admin/pre-training-videos/${videoId}/checklists`, {
        params: { lang_code },
      });
      return data.data;
    },
  },
  async mounted() {
    this.checklists = await this.fetchChecklists(
      this.$http,
      this.$route.params.id,
      this.language.code,
    );
  },
  created() {
    this.$options.tableHeaders = [
      'order',
      'state',
      'text',
      'link_text',
      'link_video',
      'thumbnail_link',
      'actions',
    ];
  },
  tableHeaders: [],
};
</script>
